import { Component } from '@theme/component';

/**
 * Custom product card: mobile shows an image-filled card with a bottom-right
 * cart button that expands into a size picker, then becomes an Add to Cart
 * button. Desktop shows the same markup laid out as image-left / info-right,
 * with sizes always visible. Cart submission is handled entirely by the
 * theme's existing `product-form-component` / `add-to-cart-component`, so
 * the cart drawer and cart bubble update instantly from the AJAX response
 * exactly like every other add-to-cart control in the theme.
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} toggleButton - The mobile cart icon toggle.
 * @property {HTMLElement} sizeList - The container of size option buttons.
 *
 * @extends {Component<Refs>}
 */
class LiminalProductCardComponent extends Component {
  requiredRefs = ['toggleButton', 'sizeList'];

  connectedCallback() {
    super.connectedCallback();

    this.variantInput = /** @type {HTMLInputElement | null} */ (
      this.querySelector('product-form-component input[name="id"]')
    );
    this.addToCartContainer = this.querySelector('add-to-cart-component');

    document.addEventListener('click', this.#handleDocumentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('click', this.#handleDocumentClick);
  }

  /**
   * Opens/closes the mobile size drawer. No-op visually on desktop, where
   * sizes are always shown via CSS, but harmless to toggle.
   * @param {Event} event
   */
  toggleSizes(event) {
    event.preventDefault();

    if (this.dataset.state === 'size-selected') return;

    const opening = this.dataset.state !== 'sizes-open';
    this.dataset.state = opening ? 'sizes-open' : 'idle';
    this.refs.toggleButton.setAttribute('aria-expanded', String(opening));
  }

  /**
   * Selects a size, wires its variant id into the hidden form input, and
   * enables the real Add to Cart button. Never falls back to a default
   * variant — the button stays disabled until this runs.
   * @param {Event} event
   */
  selectSize(event) {
    const button = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('.lpc__size') : null
    );
    if (!button || button.hasAttribute('data-unavailable')) return;

    const variantId = button.dataset.variantId;
    if (!variantId || !this.variantInput) return;

    this.refs.sizeList.querySelectorAll('.lpc__size').forEach((el) => el.setAttribute('aria-checked', 'false'));
    button.setAttribute('aria-checked', 'true');

    this.variantInput.value = variantId;
    this.addToCartContainer?.enable?.();

    this.dataset.state = 'size-selected';
    this.refs.toggleButton.setAttribute('aria-expanded', 'false');
  }

  /**
   * Closes the mobile size drawer when the user taps outside the card.
   * @param {MouseEvent} event
   */
  #handleDocumentClick = (event) => {
    if (this.dataset.state !== 'sizes-open') return;
    if (event.target instanceof Node && this.contains(event.target)) return;

    this.dataset.state = 'idle';
    this.refs.toggleButton.setAttribute('aria-expanded', 'false');
  };
}

if (!customElements.get('liminal-product-card-component')) {
  customElements.define('liminal-product-card-component', LiminalProductCardComponent);
}
